/**
 * 数据大屏 rem 动态适配工具
 *
 * 基于高度计算 rem，实现连续平滑的动态适配
 * - 基准高度: 1080px
 * - 缩放范围: 0.67 ~ 2.0 (对应 720px ~ 2160px)
 * - 边框阴影保持 px 不变
 */

// 设计稿基准高度
const DESIGN_HEIGHT = 1080

// rem 与 px 的基准比例
// 1rem = DESIGN_HEIGHT / 16 = 67.5px (在 1080 高度下)
const BASE_REM_RATIO = 16

// 最小缩放比例（对应 720px 高度）
const MIN_SCALE = 0.67

// 最大缩放比例（对应 2160px 高度）
const MAX_SCALE = 2.0

// 当前缩放比例
let currentScale = 1

/**
 * 计算并设置 html 元素的 font-size
 */
export function setRemScale(): number {
  const windowHeight = window.innerHeight
  const windowWidth = window.innerWidth

  // 基于高度计算缩放比例
  let scale = windowHeight / DESIGN_HEIGHT

  // 宽高比检查：如果屏幕比设计稿更"窄"，需要考虑宽度约束
  const currentRatio = windowWidth / windowHeight
  const designRatio = 1920 / 1080 // 16:9

  if (currentRatio < designRatio) {
    // 屏幕比设计稿窄，取较小值（宽度权重略低）
    const widthScale = windowWidth / 1920
    scale = Math.min(scale, widthScale * 1.2)
  }

  // 限制缩放范围
  scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale))

  currentScale = scale

  // 设置 html font-size
  // 1rem = (DESIGN_HEIGHT / BASE_REM_RATIO) * scale = 67.5 * scale
  const fontSize = (DESIGN_HEIGHT / BASE_REM_RATIO) * scale
  document.documentElement.style.fontSize = `${fontSize}px`

  // 开发环境下输出调试信息
  if (import.meta.env.DEV) {
    console.log(
      `[Screen Adapter] ${windowWidth}x${windowHeight} | scale: ${scale.toFixed(2)} | font-size: ${fontSize.toFixed(2)}px`,
    )
  }

  return scale
}

/**
 * 获取当前缩放比例
 */
export function getCurrentScale(): number {
  return currentScale
}

/**
 * px 转 rem（用于 JS 中的动态计算）
 * @param px 设计稿中的 px 值（1080 高度下的值）
 * @returns rem 值
 */
export function pxToRem(px: number): number {
  return px / (DESIGN_HEIGHT / BASE_REM_RATIO)
}

/**
 * px 转 rem 字符串
 * @param px 设计稿中的 px 值
 * @returns rem 字符串（如 "1.5rem"）
 */
export function pxToRemStr(px: number): string {
  return `${pxToRem(px)}rem`
}

/**
 * 根据当前缩放比例计算实际 px 值
 * 用于地图图标等需要整数 px 的场景
 * @param basePx 基准 px 值（1080 高度下的值）
 * @returns 当前缩放下的 px 值（整数）
 */
export function getScaledPx(basePx: number): number {
  return Math.round(basePx * currentScale)
}

/**
 * 初始化屏幕适配
 * @param debounceMs 防抖延迟（毫秒）
 * @returns 清理函数
 */
export function initScreenAdapter(debounceMs = 100): () => void {
  let resizeTimer: ReturnType<typeof setTimeout> | null = null

  // 初始设置
  setRemScale()

  // 监听窗口大小变化
  const handleResize = () => {
    if (resizeTimer) {
      clearTimeout(resizeTimer)
    }
    resizeTimer = setTimeout(() => {
      setRemScale()
    }, debounceMs)
  }

  window.addEventListener('resize', handleResize)

  // 返回清理函数
  return () => {
    window.removeEventListener('resize', handleResize)
    if (resizeTimer) {
      clearTimeout(resizeTimer)
    }
  }
}

// 导出配置常量
export const SCREEN_CONFIG = {
  DESIGN_HEIGHT,
  BASE_REM_RATIO,
  MIN_SCALE,
  MAX_SCALE,
  MIN_HEIGHT: Math.round(DESIGN_HEIGHT * MIN_SCALE), // 724
  MAX_HEIGHT: Math.round(DESIGN_HEIGHT * MAX_SCALE), // 2160
  BASE_REM: DESIGN_HEIGHT / BASE_REM_RATIO, // 67.5
} as const
